import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { ProfileType } from '../types';
import type { PreScreeningAnswers } from '../constants/pre-screening-questions';
import { DEFAULT_PRE_SCREENING_ANSWERS } from '../constants/pre-screening-questions';
import { getOrCreateSessionId, fetchClientInfo, clearSessionId as clearSession } from '../lib/session';
import {
  createWizardSession,
  savePrescreeningSubmission,
  saveAssessmentSubmission,
} from '../lib/supabase';

// Types for the configuration wizard
export type SeverityLevel = 'mild' | 'moderate' | 'severe';
export type PlanTier = 'plus';

export interface AssessmentAnswer {
  questionId: number;
  value: number; // 1-4 scale
}

export interface Contact {
  id: string;
  name: string;
  phone: string;
  relationship: string;
  photoUrl?: string;
  isEmergency: boolean;
  aliases?: string[];
}

export type GeoFenceSchedule = '24/7' | 'night' | 'day';

export interface GeoFenceSettings {
  enabled: boolean;
  homeLat: number;
  homeLng: number;
  radiusMeters: number;
  schedule: GeoFenceSchedule;
}

export interface HealthSettings {
  medicationReminders: boolean;
  medicationTimes?: string[];
  dailyCheckIn: boolean;
  checkInTime: string;
  sleepMonitoring?: boolean;
}

export interface ConfigurationState {
  // Step 0: Pre-Screening Questions
  preScreeningAnswers: PreScreeningAnswers;
  preScreeningComplete: boolean;

  // Step 1: Profile Selection (from Landing)
  selectedProfile: ProfileType | null;

  // Step 2: Assessment
  assessmentAnswers: AssessmentAnswer[];
  severityLevel: SeverityLevel | null;
  severityScore: number | null;

  // Step 3: Plan Selection
  selectedPlan: PlanTier | null;

  // Step 4: Layout Selection
  selectedLayoutId: string | null;

  // Step 5: Contact Setup
  contacts: Contact[];

  // Step 6: Feature Setup
  geoFenceSettings: GeoFenceSettings | null;
  healthSettings: HealthSettings | null;

  // User Details
  userDetails: {
    fullName: string;
    age: string;
    language: string;
    sexAtBirth: 'male' | 'female' | null;
  };

  // Current step tracking
  currentStep: number;
  completedSteps: number[];

  // Activation code (set after deployment)
  activationCode: string | null;

  // Session tracking for analytics
  sessionId: string | null;
  sessionInitialized: boolean;
}

interface ConfigurationContextValue extends ConfigurationState {
  // Actions
  setPreScreeningAnswers: (answers: Partial<PreScreeningAnswers>) => void;
  completePreScreening: () => Promise<void>;
  setSelectedProfile: (profile: ProfileType) => void;
  setAssessmentAnswers: (answers: AssessmentAnswer[]) => void;
  calculateSeverity: () => void;
  completeAssessment: (answers?: AssessmentAnswer[], severity?: SeverityLevel, score?: number) => Promise<void>;
  setSelectedPlan: (plan: PlanTier) => void;
  setSelectedLayout: (layoutId: string) => void;
  addContact: (contact: Omit<Contact, 'id'>) => void;
  removeContact: (contactId: string) => void;
  updateContact: (contactId: string, updates: Partial<Contact>) => void;
  setGeoFenceSettings: (settings: GeoFenceSettings) => void;
  setHealthSettings: (settings: HealthSettings) => void;
  setUserDetails: (details: ConfigurationState['userDetails']) => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  markStepComplete: (step: number) => void;
  resetConfiguration: () => void;
  canProceed: () => boolean;
  setActivationCode: (code: string) => void;
  clearWizardSession: () => void;
}

const initialState: ConfigurationState = {
  preScreeningAnswers: DEFAULT_PRE_SCREENING_ANSWERS,
  preScreeningComplete: false,
  selectedProfile: null,
  assessmentAnswers: [],
  severityLevel: null,
  severityScore: null,
  selectedPlan: null,
  selectedLayoutId: null,
  contacts: [],
  geoFenceSettings: null,
  healthSettings: null,
  userDetails: {
    fullName: '',
    age: '',
    language: 'en-US',
    sexAtBirth: null,
  },
  currentStep: 1,
  completedSteps: [],
  activationCode: null,
  sessionId: null,
  sessionInitialized: false,
};

const ConfigurationContext = createContext<ConfigurationContextValue | null>(null);

// Routes where the wizard is active and a Supabase session row is
// required. Anything outside this set is treated as marketing / auth
// surface: no /client-info call, no wizard_sessions write. Keeps the
// production table from accumulating one row per landing-page visit
// (and one per CI prerender run) and stops the 30s networkidle0
// timeout on the prerender.
function needsWizardSession(pathname: string): boolean {
  return pathname === '/configure' || pathname.startsWith('/configure/');
}

export function ConfigurationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfigurationState>(initialState);
  const sessionInitRef = useRef(false);
  const location = useLocation();
  // Use a ref to access latest state in async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Initialize session the first time the user enters a wizard route.
  // Re-runs on every navigation, but the ref guard makes the network
  // round-trip happen at most once per browser tab.
  useEffect(() => {
    if (sessionInitRef.current) return;
    if (!needsWizardSession(location.pathname)) return;
    sessionInitRef.current = true;

    const initSession = async () => {
      try {
        const sessionId = getOrCreateSessionId();
        setState(prev => ({ ...prev, sessionId }));

        // Fetch client info and create wizard session in Supabase
        const clientInfo = await fetchClientInfo();
        await createWizardSession(sessionId, clientInfo);

        setState(prev => ({ ...prev, sessionInitialized: true }));
      } catch (error) {
        // console.error, not warn: this is dropped data, not a cosmetic hiccup.
        // A warn here is what let a 26-day production outage go unnoticed.
        console.error('[WizardSession] Failed to initialize session — this session will not be recorded:', error);
        // Still mark as initialized to not block the wizard. Tracking is
        // analytics, not a gate on signup: a caregiver must never be stopped
        // from configuring a phone because a telemetry row failed to write.
        setState(prev => ({ ...prev, sessionInitialized: true }));
      }
    };

    initSession();
  }, [location.pathname]);

  const setPreScreeningAnswers = useCallback((answers: Partial<PreScreeningAnswers>) => {
    setState(prev => ({
      ...prev,
      preScreeningAnswers: { ...prev.preScreeningAnswers, ...answers },
    }));
  }, []);

  const completePreScreening = useCallback(async () => {
    setState(prev => ({ ...prev, preScreeningComplete: true }));

    // Save pre-screening answers to database immediately using ref for latest state
    const currentState = stateRef.current;
    if (currentState.sessionId) {
      try {
        await savePrescreeningSubmission(currentState.sessionId, {
          gender: 'unspecified' as const,
          ageGroup: currentState.preScreeningAnswers.ageGroup,
          livingSituation: currentState.preScreeningAnswers.livingSituation,
          hearing: currentState.preScreeningAnswers.hearing,
          vision: currentState.preScreeningAnswers.vision,
        });
      } catch (error) {
        console.error('[WizardSession] Failed to save pre-screening — answers were NOT stored:', error);
      }
    }
  }, []);

  const setSelectedProfile = useCallback((profile: ProfileType) => {
    setState(prev => ({ ...prev, selectedProfile: profile }));
  }, []);

  const setAssessmentAnswers = useCallback((answers: AssessmentAnswer[]) => {
    setState(prev => ({ ...prev, assessmentAnswers: answers }));
  }, []);

  const calculateSeverity = useCallback(() => {
    setState(prev => {
      if (prev.assessmentAnswers.length === 0) return prev;

      const totalScore = prev.assessmentAnswers.reduce((sum, a) => sum + a.value, 0);
      const avgScore = totalScore / prev.assessmentAnswers.length;

      let severity: SeverityLevel;
      if (avgScore <= 2.0) {
        severity = 'severe';
      } else if (avgScore <= 3.0) {
        severity = 'moderate';
      } else {
        severity = 'mild';
      }

      return {
        ...prev,
        severityScore: avgScore,
        severityLevel: severity,
      };
    });
  }, []);

  const completeAssessment = useCallback(async (
    answers?: AssessmentAnswer[],
    severity?: SeverityLevel,
    score?: number
  ) => {
    // Get current state values using ref for latest state
    const currentState = stateRef.current;
    if (!currentState.sessionId || !currentState.selectedProfile) return;

    // Use passed values or fall back to state
    const finalAnswers = answers || currentState.assessmentAnswers;
    const finalSeverity = severity || currentState.severityLevel;
    const finalScore = score || currentState.severityScore;

    try {
      await saveAssessmentSubmission(
        currentState.sessionId,
        currentState.selectedProfile,
        finalAnswers,
        finalSeverity || undefined,
        finalScore || undefined
      );
    } catch (error) {
      console.error('[WizardSession] Failed to save assessment — answers were NOT stored:', error);
    }
  }, []);

  const clearWizardSession = useCallback(() => {
    clearSession();
    setState(prev => ({ ...prev, sessionId: null, sessionInitialized: false }));
  }, []);

  const setSelectedPlan = useCallback((plan: PlanTier) => {
    setState(prev => ({ ...prev, selectedPlan: plan }));
  }, []);

  const setSelectedLayout = useCallback((layoutId: string) => {
    setState(prev => ({ ...prev, selectedLayoutId: layoutId }));
  }, []);

  const addContact = useCallback((contact: Omit<Contact, 'id'>) => {
    const newContact: Contact = {
      ...contact,
      id: `contact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    setState(prev => ({ ...prev, contacts: [...prev.contacts, newContact] }));
  }, []);

  const removeContact = useCallback((contactId: string) => {
    setState(prev => ({
      ...prev,
      contacts: prev.contacts.filter(c => c.id !== contactId),
    }));
  }, []);

  const updateContact = useCallback((contactId: string, updates: Partial<Contact>) => {
    setState(prev => ({
      ...prev,
      contacts: prev.contacts.map(c =>
        c.id === contactId ? { ...c, ...updates } : c
      ),
    }));
  }, []);

  const setGeoFenceSettings = useCallback((settings: GeoFenceSettings) => {
    setState(prev => ({ ...prev, geoFenceSettings: settings }));
  }, []);

  const setHealthSettings = useCallback((settings: HealthSettings) => {
    setState(prev => ({ ...prev, healthSettings: settings }));
  }, []);

  const setUserDetails = useCallback((details: ConfigurationState['userDetails']) => {
    setState(prev => ({ ...prev, userDetails: details }));
  }, []);

  const goToStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  }, []);

  const markStepComplete = useCallback((step: number) => {
    setState(prev => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(step)
        ? prev.completedSteps
        : [...prev.completedSteps, step],
    }));
  }, []);

  const resetConfiguration = useCallback(() => {
    setState(initialState);
  }, []);

  const setActivationCode = useCallback((code: string) => {
    setState(prev => ({ ...prev, activationCode: code }));
  }, []);

  const canProceed = useCallback(() => {
    const { currentStep, selectedProfile, assessmentAnswers, selectedPlan, selectedLayoutId, contacts } = state;

    switch (currentStep) {
      case 1: // Profile Selection
        return selectedProfile !== null;
      case 2: // Assessment
        return assessmentAnswers.length === 4;
      case 3: // Plan Selection
        return selectedPlan !== null;
      case 4: // Layout Selection
        return selectedLayoutId !== null;
      case 5: // Contact Setup
        return contacts.length >= 1;
      case 6: // Feature Setup
        return true; // Optional for Essential plan
      default:
        return true;
    }
  }, [state]);

  const value: ConfigurationContextValue = {
    ...state,
    setPreScreeningAnswers,
    completePreScreening,
    setSelectedProfile,
    setAssessmentAnswers,
    calculateSeverity,
    completeAssessment,
    setSelectedPlan,
    setSelectedLayout,
    addContact,
    removeContact,
    updateContact,
    setGeoFenceSettings,
    setHealthSettings,
    setUserDetails,
    goToStep,
    nextStep,
    prevStep,
    markStepComplete,
    resetConfiguration,
    canProceed,
    setActivationCode,
    clearWizardSession,
  };

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
}

export function useConfiguration() {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error('useConfiguration must be used within a ConfigurationProvider');
  }
  return context;
}

export default ConfigurationContext;
